import React, { useState } from "react";
import { Cpu, HardDrive, Laptop, MemoryStick } from "lucide-react";
import {
  DRIVE_C,
  countContents,
  getNode,
  getTrail,
  pathOf,
  sizeOf,
} from "../fs";
import { NodeIcon } from "../fileIcons";
import { PROFILE } from "../data";
import { useWindows } from "../WindowManager";
import "./properties.css";

const SYSTEM_SPECS = [
  ["Device name", "EARLPAPPI-PC"],
  ["Full device name", "EARLPAPPI-PC.localdomain"],
  ["Processor", "Web Runtime x64  ~3.20 GHz"],
  ["Installed RAM", "16.0 GB (15.8 GB usable)"],
  ["Device ID", "9F2C4A81-77E3-B0D5-1A62-EE9047C31B84"],
  ["Product ID", "00330-80000-00000-AA114"],
  ["System type", "64-bit operating system, x64-based processor"],
  ["Pen and touch", "Touch support with 10 touch points"],
];

const WINDOWS_SPECS = [
  ["Edition", "Windows 11 Pro (replica)"],
  ["Version", "24H2"],
  ["Installed on", "29/07/2026"],
  ["OS build", "26100.1742"],
  ["Experience", "Windows Feature Experience Pack 1000.26100.6.0"],
];

const kb = (n) =>
  n >= 1024 ? `${(n / 1024).toFixed(1)} MB` : `${Math.round(n)} KB`;

function SystemProperties() {
  return (
    <>
      <div className="pr-hero">
        <span className="pr-hero__icon pr-hero__icon--system">
          <Laptop size={30} />
        </span>
        <div>
          <div className="pr-hero__name">EARLPAPPI-PC</div>
          <div className="pr-hero__kind">
            Windows 11 Pro · Browser-hosted workstation
          </div>
        </div>
      </div>

      <div className="pr-quick">
        <div className="pr-quick__tile">
          <Cpu size={16} />
          <div>
            <strong>Web Runtime x64</strong>
            <span>Processor</span>
          </div>
        </div>
        <div className="pr-quick__tile">
          <MemoryStick size={16} />
          <div>
            <strong>16.0 GB</strong>
            <span>Installed RAM</span>
          </div>
        </div>
        <div className="pr-quick__tile">
          <HardDrive size={16} />
          <div>
            <strong>
              {DRIVE_C.totalGb - DRIVE_C.usedGb} GB free
            </strong>
            <span>of {DRIVE_C.totalGb} GB on C:</span>
          </div>
        </div>
      </div>

      <h3 className="pr-h">Device specifications</h3>
      <dl className="pr-rows">
        {SYSTEM_SPECS.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>

      <h3 className="pr-h">Windows specifications</h3>
      <dl className="pr-rows">
        {WINDOWS_SPECS.map(([k, v]) => (
          <div key={k}>
            <dt>{k}</dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>

      <h3 className="pr-h">Computer name, domain and workgroup</h3>
      <dl className="pr-rows">
        <div>
          <dt>Computer name</dt>
          <dd>EARLPAPPI-PC</dd>
        </div>
        <div>
          <dt>Workgroup</dt>
          <dd>WORKGROUP</dd>
        </div>
        <div>
          <dt>Registered owner</dt>
          <dd>{PROFILE.name}</dd>
        </div>
      </dl>
    </>
  );
}

function DriveProperties({ node }) {
  const usedPct = Math.round((node.usedGb / node.totalGb) * 100);
  return (
    <>
      <div className="pr-hero">
        <span className="pr-hero__icon pr-hero__icon--drive">
          <HardDrive size={28} />
        </span>
        <div>
          <div className="pr-hero__name">{node.name}</div>
          <div className="pr-hero__kind">Local Disk · NTFS</div>
        </div>
      </div>

      <div className="pr-pie">
        <div
          className="pr-pie__chart"
          style={{
            background: `conic-gradient(var(--accent) 0 ${usedPct}%, var(--divider) ${usedPct}% 100%)`,
          }}
          aria-hidden="true"
        />
        <dl className="pr-rows pr-rows--flush">
          <div>
            <dt>
              <i className="pr-key pr-key--used" /> Used space
            </dt>
            <dd>{node.usedGb} GB</dd>
          </div>
          <div>
            <dt>
              <i className="pr-key pr-key--free" /> Free space
            </dt>
            <dd>{node.totalGb - node.usedGb} GB</dd>
          </div>
          <div>
            <dt>Capacity</dt>
            <dd>{node.totalGb} GB</dd>
          </div>
        </dl>
      </div>
    </>
  );
}

function FileProperties({ node, apps }) {
  const isFolder = node.type !== "file";
  const trail = getTrail(node.id);
  const parent = trail[trail.length - 1];
  const contents = isFolder ? countContents(node) : null;
  const opener = apps.find((a) => a.id === node.open?.app);
  const bytes = sizeOf(node);

  return (
    <>
      <div className="pr-hero">
        <span className="pr-hero__icon">
          <NodeIcon node={node} size={30} />
        </span>
        <div>
          <div className="pr-hero__name">{node.name}</div>
          <div className="pr-hero__kind">{node.kind}</div>
        </div>
      </div>

      <dl className="pr-rows">
        <div>
          <dt>Type of file</dt>
          <dd>
            {node.kind}
            {node.ext ? ` (.${node.ext})` : ""}
          </dd>
        </div>
        {opener && (
          <div>
            <dt>Opens with</dt>
            <dd>{opener.title}</dd>
          </div>
        )}
        <div>
          <dt>Location</dt>
          <dd className="pr-path">{parent ? pathOf(parent.id) : "This PC"}</dd>
        </div>
        <div>
          <dt>Size</dt>
          <dd>{isFolder ? kb(bytes) : node.size || "0 KB"}</dd>
        </div>
        {isFolder && (
          <div>
            <dt>Contains</dt>
            <dd>
              {contents.files} files, {contents.folders} folders
            </dd>
          </div>
        )}
        <div>
          <dt>Created</dt>
          <dd>{node.modified || "—"}</dd>
        </div>
        <div>
          <dt>Modified</dt>
          <dd>{node.modified || "—"}</dd>
        </div>
        {node.deletedFrom && (
          <div>
            <dt>Deleted</dt>
            <dd>{node.deletedAt}</dd>
          </div>
        )}
      </dl>

      <h3 className="pr-h">Attributes</h3>
      <div className="pr-attrs">
        <label>
          <input type="checkbox" defaultChecked={Boolean(node.protected)} />
          Read-only
        </label>
        <label>
          <input type="checkbox" />
          Hidden
        </label>
      </div>
    </>
  );
}

export default function PropertiesApp({ payload, winId }) {
  const { apps, close } = useWindows();
  const [tab, setTab] = useState("general");
  const isSystem = payload?.system || !payload?.nodeId;
  const node = isSystem ? null : getNode(payload.nodeId);
  const isDrive = node?.type === "drive";

  const tabs = isSystem
    ? [{ id: "general", label: "About" }]
    : [
        { id: "general", label: "General" },
        { id: "security", label: "Security" },
        { id: "details", label: "Details" },
      ];

  return (
    <div className="pr">
      <div className="pr-tabs" role="tablist">
        {tabs.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            className={`pr-tab ${tab === t.id ? "pr-tab--on" : ""}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="pr-body win-scroll">
        {tab === "general" &&
          (isSystem ? (
            <SystemProperties />
          ) : isDrive ? (
            <DriveProperties node={node} />
          ) : (
            <FileProperties node={node} apps={apps} />
          ))}

        {tab === "security" && (
          <>
            <h3 className="pr-h" style={{ marginTop: 4 }}>
              Group or user names
            </h3>
            <div className="pr-list">
              {["SYSTEM", "Administrators (EARLPAPPI-PC\\Administrators)", "Users (EARLPAPPI-PC\\Users)"].map(
                (name) => (
                  <div className="pr-list__row" key={name}>
                    {name}
                  </div>
                )
              )}
            </div>
            <h3 className="pr-h">Permissions for SYSTEM</h3>
            <dl className="pr-rows">
              {["Full control", "Modify", "Read & execute", "Write"].map((p) => (
                <div key={p}>
                  <dt>{p}</dt>
                  <dd>Allow</dd>
                </div>
              ))}
            </dl>
          </>
        )}

        {tab === "details" && (
          <dl className="pr-rows" style={{ marginTop: 4 }}>
            <div>
              <dt>Name</dt>
              <dd>{node?.name}</dd>
            </div>
            <div>
              <dt>Item type</dt>
              <dd>{node?.kind}</dd>
            </div>
            <div>
              <dt>Folder path</dt>
              <dd className="pr-path">{pathOf(node?.id)}</dd>
            </div>
            <div>
              <dt>Owner</dt>
              <dd>EARLPAPPI-PC\earlpappi</dd>
            </div>
            <div>
              <dt>Computer</dt>
              <dd>EARLPAPPI-PC (this PC)</dd>
            </div>
          </dl>
        )}
      </div>

      <div className="pr-foot">
        <button className="win-btn win-btn--accent" onClick={() => close(winId)}>
          OK
        </button>
        <button className="win-btn" onClick={() => close(winId)}>
          Cancel
        </button>
        <button className="win-btn" disabled>
          Apply
        </button>
      </div>
    </div>
  );
}
